import { getLanguageCodeByLocale } from 'common/enums/LanguageCodeEnum'
import StyleWrapper from 'publisher/components/core/StyleWrapper'
import FieldErrors from 'publisher/components/FieldErrors'
import { typedPageSelectors, useOptIn, usePage } from 'publisher/store'
import { getAvailableCountries } from 'publisher/store/opt-in/optInSelectors'
import { getCustomerCountry } from 'publisher/store/optIn/optInSelectors'
import getCountries from 'publisher/utils/getCountries'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { SelectNew } from 'src/shared/components/select-new'
import { ContactUsFieldEntityInterface } from 'src/shared/entities/contact-us-field/contact-us-field-entity.schema'
import { FieldEntityInterface } from 'src/shared/entities/field/field-entity.schema'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { FieldSubEntityInterface } from 'src/shared/schemas/fields/field-sub-entity.schema'

type BasePublisherCountryFieldProps = {
  entity:
    | FieldEntityInterface
    | ContactUsFieldEntityInterface
    | FieldSubEntityInterface
  value: string | undefined
  onChange: (value: string) => void
  errors: string[]
}

export const BasePublisherCountryField = ({
  entity,
  value,
  onChange,
  errors,
}: BasePublisherCountryFieldProps) => {
  const customerCountry = useOptIn(getCustomerCountry) || undefined
  const [countries, setCountries] = useState<Record<string, string>[]>([])
  const locale = usePage(state => typedPageSelectors.getLocale(state))
  const availableCountries = useOptIn(state => getAvailableCountries(state))
  const availableCountriesCodes = Object.keys(availableCountries)
  const { t } = useTypedTranslation()

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value)
  }

  useEffect(() => {
    if (availableCountriesCodes.length === 0) {
      setCountries(getCountries(getLanguageCodeByLocale(locale)))
    } else {
      setCountries(
        Object.keys(availableCountries).map(code => ({
          code,
          name: availableCountries[code],
        })),
      )
      if (availableCountriesCodes.length === 1) {
        onChange(availableCountriesCodes[0])
      }
    }
  }, [locale])

  useEffect(() => {
    if (
      countries.length !== 0 &&
      !value &&
      customerCountry &&
      countries.some(({ code }) => code === customerCountry)
    ) {
      onChange(customerCountry)
    }
  }, [countries.length, customerCountry])

  return (
    <StyleWrapper
      margin={entity.margin}
      mobileMargin={entity.mobileMargin}
      appearance={entity.appearance}
    >
      <SelectNew
        value={value}
        onChange={handleChange}
        padding={entity.padding}
        mobilePadding={entity.mobilePadding}
        textAlign={entity.textAlign}
        mobileTextAlign={entity.mobileTextAlign}
        lineHeight={entity.lineHeight}
        mobileLineHeight={entity.mobileLineHeight}
        border={entity.border}
        mobileBorder={entity.mobileBorder}
        backgroundColor={entity.backgroundColor}
        mobileBackgroundColor={entity.mobileBackgroundColor}
        color={entity.color}
        mobileColor={entity.mobileColor}
        fontSize={entity.fontSize}
        mobileFontSize={entity.mobileFontSize}
        boxShadow={entity.boxShadow}
        mobileBoxShadow={entity.mobileBoxShadow}
      >
        <option value="">{t('entities.field.country.placeholder')}</option>
        {countries.map(({ code, name }) => (
          <option key={code} value={code}>
            {name}
          </option>
        ))}
      </SelectNew>
      <FieldErrors errors={errors} />
    </StyleWrapper>
  )
}
